import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Check } from 'lucide-react';
import { MessageDisplay } from '@/hooks/ai-agents/types/chat-hook-types';
import { cn } from '@/lib/utils';

interface CopyMessageButtonProps {
  message: MessageDisplay;
  className?: string;
}

/**
 * Button that appears on AI messages to copy the message content to clipboard
 * Only shows for assistant messages
 */
export const CopyMessageButton: React.FC<CopyMessageButtonProps> = ({
  message,
  className
}) => {
  const [copyState, setCopyState] = useState<'idle' | 'success'>('idle');
  
  // Only show for assistant messages
  const isAssistant = message.role === 'assistant';
  
  if (!isAssistant || !message.content) {
    return null;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content || '');
      setCopyState('success');
      
      // Reset to idle after 1.5 seconds
      setTimeout(() => {
        setCopyState('idle');
      }, 1500);
      
    } catch (error) {
      console.error('[CopyMessageButton] Failed to copy message:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = message.content || '';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      setCopyState('success');
      setTimeout(() => {
        setCopyState('idle');
      }, 1500);
    }
  };

  const getButtonContent = () => {
    switch (copyState) {
      case 'success':
        return <Check className="h-3 w-3" />;
      default:
        return <Copy className="h-3 w-3" />;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 10, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: 10, scale: 0.95 }}
        transition={{ 
          type: 'spring', 
          stiffness: 500, 
          damping: 30,
          duration: 0.2 
        }}
        className={cn("flex items-center", className)}
      >
        <button
          onClick={handleCopy}
          className={cn(
            // Base AI function button styles
            "inline-flex items-center justify-center",
            "h-5 w-5 rounded-md", // Square button for icon-only
            "transition-all duration-200 ease-out",
            "focus:outline-none focus:ring-2 focus:ring-offset-0",
            // Default black theme with dark grey icon
            copyState === 'idle' && [
              "bg-black/80 dark:bg-black/80",
              "border border-black/20 dark:border-black/40",
              "text-slate-600 dark:text-slate-500",
              "hover:bg-black dark:hover:bg-black",
              "hover:border-black/40 dark:hover:border-black/60",
              "hover:text-slate-500 dark:hover:text-slate-400",
              "focus:ring-black/20"
            ],
            // Success state
            copyState === 'success' && [
              "bg-gradient-to-r from-green-500/15 to-emerald-500/15 dark:from-green-400/15 dark:to-emerald-400/15",
              "border border-green-300/40 dark:border-green-600/40",
              "text-green-600 dark:text-green-400"
            ]
          )}
          title={copyState === 'success' ? 'Copied!' : 'Copy message'}
        >
          {getButtonContent()}
        </button>
      </motion.div>
    </AnimatePresence>
  );
}; 